/**
 * BulkEmailPreviewDialog — P0.18.2.2 Operational Document Hub Completion (Aug 2026).
 *
 * Preview dialoog vóór bulk-verzending. Toont:
 *   • Aantal geselecteerde documenten
 *   • Resolved ontvangers per document
 *   • Skip-lijst (missende adressen + reden)
 *   • Onderwerp override
 *   • Introductietekst override
 *   • CC / BCC velden
 *   • Totale bestandsgrootte
 *   • Waarschuwingen
 *
 * Gebruikt POST /api/admin/document-hub/bulk-email/preview om dry-run
 * data op te halen. Bevestigen doet POST /api/admin/document-hub/bulk-email.
 *
 * data-testids:
 *   bulk-email-preview-modal
 *   bulk-email-preview-close
 *   bulk-email-preview-loading
 *   bulk-email-preview-summary
 *   bulk-email-preview-item-{index}
 *   bulk-email-preview-skipped-{index}
 *   bulk-email-preview-subject-input
 *   bulk-email-preview-intro-input
 *   bulk-email-preview-cc-input
 *   bulk-email-preview-bcc-input
 *   bulk-email-preview-recipient-override
 *   bulk-email-preview-confirm-send
 *   bulk-email-preview-cancel
 */
import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import {
  X, Mail, Loader2, AlertTriangle, Send, CheckCircle2, User,
  FileText, Package, Building2,
} from "lucide-react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { useGlobalLoading } from "../../../context/GlobalLoadingProvider";

function fmtSize(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

const SKIP_LABELS_NL = {
  skipped_missing_recipient: "Geen ontvanger e-mailadres",
  skipped_invalid_document: "Document kan niet gegenereerd worden",
};

export default function BulkEmailPreviewDialog({ documentIds, onClose, onSent }) {
  const { runTask } = useGlobalLoading();
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [subjectOverride, setSubjectOverride] = useState("");
  const [introOverride, setIntroOverride] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [recipientOverride, setRecipientOverride] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi.post(
          "/admin/document-hub/bulk-email/preview",
          { document_ids: documentIds }
        );
        setPreview(data);
      } catch (err) {
        setError(formatApiError(err) || "Preview kon niet worden geladen");
      } finally {
        setLoading(false);
      }
    })();
  }, [documentIds]);

  const operationId = useMemo(
    () => `bulk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    []
  );

  // P0.18.2.2 fix — recipient_override "verhuist" skipped_missing_recipient
  // items terug naar de sendable-lijst zodat operators bij testverzending
  // ("stuur alles naar mijn eigen adres") niet 0 e-mails krijgen.
  const overrideActive = recipientOverride.trim() !== "";
  const effectiveItems = useMemo(() => {
    if (!preview) return [];
    if (!overrideActive) return preview.items || [];
    // Voeg items uit skipped die skipped_missing_recipient waren toe;
    // laat invalid_document skips WEL in de skip-lijst.
    const revived = (preview.skipped || [])
      .filter((s) => s.status === "skipped_missing_recipient")
      .map((s) => ({
        document_id: s.document_id,
        type: s.type,
        type_label: s.type_label || s.type,
        filename: s.filename || s.document_id,
        size_bytes: s.size_bytes || 0,
        recipient_email: recipientOverride.trim(),
        recipient_name: null,
        resolution_source: "override",
        customer_id: s.customer_id,
        customer_name: s.customer_name,
        w_number: s.w_number,
      }));
    return [...(preview.items || []), ...revived];
  }, [preview, overrideActive, recipientOverride]);

  const effectiveSkipped = useMemo(() => {
    if (!preview) return [];
    if (!overrideActive) return preview.skipped || [];
    return (preview.skipped || []).filter(
      (s) => s.status !== "skipped_missing_recipient"
    );
  }, [preview, overrideActive]);

  const effectiveSendableCount = effectiveItems.length;
  const effectiveSkippedCount = effectiveSkipped.length;
  const effectiveTotalSize = effectiveItems.reduce(
    (sum, it) => sum + (it.size_bytes || 0), 0
  );

  const handleSend = async () => {
    if (effectiveSendableCount === 0) {
      toast.error("Er zijn geen documenten met geldige ontvangers om te verzenden");
      return;
    }
    setSending(true);
    try {
      await runTask({
        id: `bulk-email-send-${operationId}`,
        message: `E-mails worden verzonden (${effectiveSendableCount} documenten)…`,
        successMessage: "Bulk-e-mail voltooid",
        run: async () => {
          const payload = {
            document_ids: effectiveItems.map((it) => it.document_id),
            operation_id: operationId,
          };
          if (recipientOverride.trim()) payload.recipient_override = recipientOverride.trim();
          if (subjectOverride.trim()) payload.subject_override = subjectOverride.trim();
          if (introOverride.trim()) payload.intro_override = introOverride.trim();
          const ccList = cc.split(",").map((s) => s.trim()).filter(Boolean);
          const bccList = bcc.split(",").map((s) => s.trim()).filter(Boolean);
          if (ccList.length) payload.cc = ccList;
          if (bccList.length) payload.bcc = bccList;

          const { data } = await adminApi.post(
            "/admin/document-hub/bulk-email", payload
          );
          const sent = data.sent || 0;
          const failed = data.failed || 0;
          const skipped = (data.skipped_missing_recipient || 0)
            + (data.skipped_invalid_document || 0);
          const suppressed = data.suppressed || 0;

          if (failed > 0) {
            toast.warning(`${sent} verzonden, ${failed} mislukt, ${skipped + suppressed} overgeslagen`);
          } else if (sent > 0) {
            toast.success(`${sent} documenten succesvol verzonden`);
          } else {
            toast.info("Geen documenten verzonden");
          }
          onSent?.(data);
          onClose();
        },
      });
    } catch (e) {
      if (e?.message !== "DUPLICATE_TASK") {
        toast.error(formatApiError(e) || "Bulk-e-mail mislukt");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      data-testid="bulk-email-preview-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full max-w-4xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-[#082c50] text-white">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            <div>
              <div className="text-sm font-bold">Bulk-e-mail voorbereiden</div>
              <div className="text-[10px] opacity-70">
                {preview
                  ? `${effectiveSendableCount} verzendbaar · ${effectiveSkippedCount} overgeslagen${overrideActive ? " · override actief" : ""}`
                  : "Bezig met voorbereiden…"}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/10 rounded"
            data-testid="bulk-email-preview-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div
              className="flex items-center justify-center h-40 flex-col gap-2"
              data-testid="bulk-email-preview-loading"
            >
              <Loader2 className="w-5 h-5 animate-spin text-[#082c50]" />
              <p className="text-xs text-gray-500">Ontvangers worden gecontroleerd…</p>
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          ) : preview ? (
            <>
              {/* Samenvatting */}
              <div
                className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4"
                data-testid="bulk-email-preview-summary"
              >
                <div className="bg-emerald-50 border border-emerald-200 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold">Verzendbaar</div>
                  <div className="text-lg font-bold text-emerald-900">{effectiveSendableCount}</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-amber-700 font-bold">Overgeslagen</div>
                  <div className="text-lg font-bold text-amber-900">{effectiveSkippedCount}</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-blue-700 font-bold">Totaal</div>
                  <div className="text-lg font-bold text-blue-900">{preview.total_selected}</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 p-2">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 font-bold">Bijlage-grootte</div>
                  <div className="text-lg font-bold text-gray-900">{fmtSize(effectiveTotalSize)}</div>
                </div>
              </div>

              {/* Optionele overrides */}
              <div className="space-y-2 mb-4 p-3 border border-gray-200 bg-gray-50">
                <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide">
                  Optionele overrides
                </div>
                <input
                  value={recipientOverride}
                  onChange={(e) => setRecipientOverride(e.target.value)}
                  placeholder="Ontvanger override (stuurt alles naar dit adres — voor test)"
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-none"
                  data-testid="bulk-email-preview-recipient-override"
                />
                <input
                  value={subjectOverride}
                  onChange={(e) => setSubjectOverride(e.target.value)}
                  placeholder="Onderwerp (laat leeg voor standaard per document)"
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-none"
                  data-testid="bulk-email-preview-subject-input"
                />
                <textarea
                  value={introOverride}
                  onChange={(e) => setIntroOverride(e.target.value)}
                  rows={2}
                  placeholder="Begeleidende tekst (laat leeg voor standaard)"
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-none"
                  data-testid="bulk-email-preview-intro-input"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    placeholder="CC (kommagescheiden)"
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-none"
                    data-testid="bulk-email-preview-cc-input"
                  />
                  <input
                    value={bcc}
                    onChange={(e) => setBcc(e.target.value)}
                    placeholder="BCC (kommagescheiden)"
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-none"
                    data-testid="bulk-email-preview-bcc-input"
                  />
                </div>
              </div>

              {/* Verzendbare lijst */}
              {effectiveItems && effectiveItems.length > 0 && (
                <div className="mb-4">
                  <div className="text-[10px] uppercase font-bold text-emerald-700 tracking-wide mb-2 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3" /> Wordt verzonden ({effectiveItems.length})
                  </div>
                  <div className="border border-emerald-200 divide-y divide-emerald-100 bg-emerald-50/30">
                    {effectiveItems.map((it, i) => (
                      <div
                        key={it.document_id}
                        className="grid grid-cols-12 items-center gap-2 px-2.5 py-1.5 text-xs"
                        data-testid={`bulk-email-preview-item-${i}`}
                      >
                        <div className="col-span-3 flex items-center gap-1.5">
                          <FileText className="w-3 h-3 text-emerald-700" />
                          <span className="font-mono truncate">{it.filename}</span>
                        </div>
                        <div className="col-span-4 truncate flex items-center gap-1.5">
                          <User className="w-3 h-3 text-gray-500" />
                          {recipientOverride ? (
                            <span className="text-blue-700 font-mono">
                              {recipientOverride} <span className="text-[9px] uppercase">(override)</span>
                            </span>
                          ) : (
                            <span className="font-mono">{it.recipient_email}</span>
                          )}
                        </div>
                        <div className="col-span-3 truncate flex items-center gap-1.5">
                          <Building2 className="w-3 h-3 text-gray-500" />
                          <span className="truncate">{it.customer_name || it.recipient_name || "-"}</span>
                        </div>
                        <div className="col-span-2 text-right text-gray-500 font-mono">
                          {fmtSize(it.size_bytes)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Skipped lijst */}
              {effectiveSkipped && effectiveSkipped.length > 0 && (
                <div className="mb-2">
                  <div className="text-[10px] uppercase font-bold text-amber-700 tracking-wide mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-3 h-3" /> Wordt overgeslagen ({effectiveSkipped.length})
                  </div>
                  <div className="border border-amber-200 divide-y divide-amber-100 bg-amber-50/30">
                    {effectiveSkipped.map((it, i) => (
                      <div
                        key={it.document_id}
                        className="grid grid-cols-12 items-center gap-2 px-2.5 py-1.5 text-xs"
                        data-testid={`bulk-email-preview-skipped-${i}`}
                      >
                        <div className="col-span-4 flex items-center gap-1.5">
                          <Package className="w-3 h-3 text-amber-700" />
                          <span className="font-mono truncate">
                            {it.filename || it.document_id}
                          </span>
                        </div>
                        <div className="col-span-5 truncate text-amber-800">
                          {it.reason || SKIP_LABELS_NL[it.status] || it.status}
                        </div>
                        <div className="col-span-3 truncate text-gray-500">
                          {it.customer_name || it.w_number || "-"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
          <div className="text-[10px] text-gray-500">
            {preview
              ? `Bulk-e-mail wordt via Resend verzonden met 5 mails/sec throttling`
              : ""}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-xs uppercase font-bold text-gray-600 hover:bg-gray-100 border border-gray-300"
              data-testid="bulk-email-preview-cancel"
            >
              Annuleren
            </button>
            <button
              onClick={handleSend}
              disabled={loading || sending || !preview || effectiveSendableCount === 0}
              className="px-4 py-1.5 text-xs uppercase font-bold text-white bg-[#0055FF] hover:bg-[#0044CC] disabled:bg-gray-300 flex items-center gap-1.5"
              data-testid="bulk-email-preview-confirm-send"
            >
              {sending
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Send className="w-3 h-3" />}
              Verzend {preview ? `${effectiveSendableCount} e-mails` : "…"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
